import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Select, Tooltip } from '@radix-ui/themes';

import { useFetchCampaigns } from '../../campaigns/hooks/useFetchCampaigns';
import styles from './campaign_filter.module.css';

export const CampaignFilter = ({ campaignId, setCampaignId }) => {
  const { campaigns } = useFetchCampaigns({
    with_live_post: true,
    show_all: true,
  });
  const { t } = useTranslation('reports');
  const { search } = window.location;
  const queryParams = new URLSearchParams(search);
  const campaignIdFromUrl = queryParams.get('campaign_id');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (campaignIdFromUrl) {
      setCampaignId(campaignIdFromUrl);
    }
  }, [campaignIdFromUrl, setCampaignId]);

  const handleChange = value => {
    setCampaignId(value);
  };

  const handleClear = e => {
    e.preventDefault();
    e.stopPropagation();
    setCampaignId(null);
  };

  const groupedOptions = useMemo(() => {
    if (!campaigns || campaigns.length === 0) return [];

    const activeCampaigns = campaigns.filter(campaign => !campaign.isArchived);
    const archivedCampaigns = campaigns.filter(campaign => campaign.isArchived);

    const campaignGroups = [
      {
        campaigns: activeCampaigns,
        labelKey: 'components.campaignFilter.activeCampaigns',
        isArchived: false,
      },
      {
        campaigns: archivedCampaigns,
        labelKey: 'components.campaignFilter.archivedCampaigns',
        isArchived: true,
      },
    ];

    return campaignGroups
      .filter(group => group.campaigns.length > 0)
      .map(group => ({
        label: t(group.labelKey),
        options: group.campaigns.map(campaign => ({
          value: campaign.uuid,
          label: campaign.name,
          isArchived: group.isArchived,
        })),
      }));
  }, [campaigns, t]);

  if (!campaigns || campaigns.length === 0) return null;

  return (
    <>
      <Flex direction="row" gap="3" align="center">
        <div className={styles.selectWrapper}>
          <Select.Root
            key={campaignId}
            size="3"
            value={campaignId || undefined}
            onValueChange={handleChange}
            open={isOpen}
            onOpenChange={setIsOpen}
          >
            {campaignId && (
              <button
                id="clearButton"
                type="button"
                onClick={handleClear}
                onMouseDown={handleClear}
                className={styles.clearButton}
                style={{
                  zIndex: isOpen ? 0 : 1,
                }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            )}
            <div className={styles.triggerContainer}>
              <Select.Trigger
                placeholder={t('components.campaignFilter.selectPlaceholder')}
                className={`${styles.trigger} ${styles.selectItem}`}
              ></Select.Trigger>
            </div>
            <Select.Content position="popper" side="bottom">
              {groupedOptions.map((group, groupIndex) => (
                <Select.Group key={groupIndex}>
                  {group.label ===
                    t('components.campaignFilter.archivedCampaigns') && (
                    <Select.Separator />
                  )}
                  <Select.Label>{group.label}</Select.Label>
                  {group.options.map(option => (
                    <Select.Item
                      key={option.value}
                      value={option.value}
                      className={`${
                        option.isArchived ? styles.archivedItem : ''
                      } ${styles.selectItem}`}
                    >
                      {option.label}
                    </Select.Item>
                  ))}
                </Select.Group>
              ))}
            </Select.Content>
          </Select.Root>
        </div>
        <Tooltip content={t('components.campaignFilter.tooltip')}>
          <i className="fa-regular fa-circle-info" />
        </Tooltip>
      </Flex>
    </>
  );
};
